import axios from "axios";
import { API_BASE_PATH } from "../../environment";

const PROJECT_MAPPING_BASE_URL = `${API_BASE_PATH}/project-mapping`;

// ---------- Interfaces ----------

export interface Employee {
  employee_id: string;
  username: string;
  department?: string;
  designation?: string;
  email?: string;
}

export interface ProjectMapping {
  project_member: string[] | null;
  project_lead: string[] | null;
  project_manager: string[] | null;
  employee_id: string | null;
  project_id: string;
  employee: Employee | null;
}

export interface SubmitMappingPayload {
  project_member: string | string[];
  project_lead: string | string[];
  project_manager: string | string[];
  employee_id: string;
}

export const fetchProjectMappingLeaders = async (): Promise<Employee[]> => {
  try {
    const response = await axios.get(`${PROJECT_MAPPING_BASE_URL}/all/manager/leader`);
    return response.data
      .filter((item: ProjectMapping) => item.employee)
      .map((item: ProjectMapping) => item.employee!);
  } catch (error: any) {
    console.error("Failed to fetch project leaders:", error.message || error);
    return [];
  }
};

export const fetchAllProjectMappings = async (): Promise<ProjectMapping[]> => {
  try {
    const response = await axios.get(`${PROJECT_MAPPING_BASE_URL}`);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch project mappings:", error.message || error);
    return [];
  }
};

export const fetchProjectMappingById = async (
  projectId: string
): Promise<ProjectMapping> => {
  try {
    const response = await axios.get(`${PROJECT_MAPPING_BASE_URL}/${projectId}`);
    return response.data;
  } catch (error: any) {
    console.error(`Failed to fetch mapping for project ID ${projectId}:`, error.message || error);
    throw new Error("Could not fetch project mapping.");
  }
};

// ---------- POST Request ----------
export const createProjectMapping = async (
  payload: SubmitMappingPayload
): Promise<any> => {
  try {
    const data = new URLSearchParams({
      project_member: Array.isArray(payload.project_member)
        ? payload.project_member.join(",")
        : payload.project_member,
      project_lead: Array.isArray(payload.project_lead)
        ? payload.project_lead.join(",")
        : payload.project_lead,
      project_manager: Array.isArray(payload.project_manager)
        ? payload.project_manager.join(",")
        : payload.project_manager,
      employee_id: payload.employee_id,
    });

    const response = await axios.post(`${PROJECT_MAPPING_BASE_URL}`, data, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to create project mapping:", error.message || error);
    throw new Error("Could not create project mapping.");
  }
};

export const updateProjectMapping = async (
  projectId: string,
  payload: SubmitMappingPayload
): Promise<any> => {
  try {
    const data = new URLSearchParams({
      project_member: Array.isArray(payload.project_member)
        ? payload.project_member.join(",")
        : payload.project_member,
      project_lead: Array.isArray(payload.project_lead)
        ? payload.project_lead.join(",")
        : payload.project_lead,
      project_manager: Array.isArray(payload.project_manager)
        ? payload.project_manager.join(",")
        : payload.project_manager,
      employee_id: payload.employee_id,
    });

    const response = await axios.put(
      `${PROJECT_MAPPING_BASE_URL}/${projectId}`,
      data,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error(`Failed to update project mapping ${projectId}:`, error.message || error);
    throw new Error("Could not update project mapping.");
  }
};

export const updateProjectMember = async (
  projectId: string,
  project_member: string
): Promise<any> => {
  try {
    const data = new URLSearchParams({ project_member });

    const response = await axios.put(
      `${PROJECT_MAPPING_BASE_URL}/update_member/${projectId}`,
      data,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error(`Failed to update project member for ${projectId}:`, error.message || error);
    throw new Error("Could not update project member.");
  }
};

export const updateProjectLead = async (
  projectId: string,
  project_lead: string
): Promise<any> => {
  try {
    const data = new URLSearchParams({ project_lead });

    const response = await axios.put(
      `${PROJECT_MAPPING_BASE_URL}/update_lead/${projectId}`,
      data,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error(`Failed to update project lead for ${projectId}:`, error.message || error);
    throw new Error("Could not update project lead.");
  }
};

export const updateProjectManager = async (
  projectId: string,
  project_manager: string
): Promise<any> => {
  try {
    const data = new URLSearchParams({ project_manager });

    const response = await axios.put(
      `${PROJECT_MAPPING_BASE_URL}/update_manager/${projectId}`,
      data,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error(`Failed to update project manager for ${projectId}:`, error.message || error);
    throw new Error("Could not update project manager.");
  }
};

export const deleteProjectMapping = async (
  projectId: string
): Promise<any> => {
  try {
    const response = await axios.delete(`${PROJECT_MAPPING_BASE_URL}/${projectId}`);
    return response.data;
  } catch (error: any) {
    console.error(`Failed to delete project mapping ${projectId}:`, error.message || error);
    throw new Error("Could not delete project mapping.");
  }
};

export const fetchProjectEmployees = async (): Promise<Employee[]> => {
  try {
    const response = await axios.get(`${PROJECT_MAPPING_BASE_URL}/all/employee`);
    const data = response.data;

    return data[0].employee_data.map((emp: any): Employee => ({
      employee_id: emp.employee_id ?? "",
      username: emp.username ?? "",
      designation: emp.designation ?? "",
      department: emp.department ?? "",
      email: emp.email ?? "",
    }));
  } catch (error: any) {
    console.error("Failed to fetch project employees:", error.message || error);
    return [];
  }
};
